import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Check, Clock3, Database, RefreshCw, ShieldCheck } from "lucide-react";
import type { LeadBackfillJob, LeadBackfillValues } from "@shared/lead-backfill";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { CampaignReadiness, LeadDetailReadinessPanel } from "@/components/lead-detail-readiness";
import { leadRecoveryRequest as requestJson } from "@/lib/lead-recovery-request";

type BackfillContext = {
  brandId: string;
  brandName: string;
  accountId: string;
  accountName: string;
  eventName: string;
  definitionId: string;
  timezone: string;
  environment: "development" | "production";
  since: string;
  until: string;
};

type Props = {
  operation?: "account" | "campaign_detail";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: string;
  accountId: string;
  onCommitted: () => void | Promise<void>;
  onCloseAutoFocus?: (event: Event) => void;
};

const ACTIVE = new Set<LeadBackfillJob["status"]>(["queued_preview", "previewing", "queued_apply", "applying"]);
const statusCopy: Record<LeadBackfillJob["status"], string> = {
  queued_preview: "Preview queued",
  previewing: "Building preview",
  review: "Ready for review",
  queued_apply: "Backfill queued",
  applying: "Writing eligible days",
  completed: "Backfill complete",
  partial: "Partially committed",
  interrupted: "Interrupted safely",
  failed: "Backfill failed",
};

function money(value: number | null, currency: string | null) {
  if (value == null) return "—";
  if (!currency) return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 2 }).format(value); }
  catch { return `${currency} ${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}`; }
}

function count(value: number | null) {
  return value == null ? "—" : new Intl.NumberFormat().format(value);
}

function valuesSummary(value: LeadBackfillValues | null) {
  if (!value) return "—";
  return `${value.currency || "Currency unavailable"} · ${count(value.leads)} leads · ${money(value.spend, value.currency)} · ${count(value.impressions)} impr. · ${count(value.clicks)} clicks`;
}

function eligibleTotals(job: LeadBackfillJob, key: "existing" | "proposed") {
  const eligible = job.days.filter(day => day.status !== "blocked" && day.proposed != null);
  const values = eligible.map(day => day[key]);
  const sum = (field: keyof LeadBackfillValues) => {
    if (values.length === 0 || values.some(value => !value || value[field] == null)) return null;
    return (values as LeadBackfillValues[]).reduce((total, value) => total + (value[field] as number), 0);
  };
  const currencies = [...new Set(values.map(value => value?.currency).filter((currency): currency is string => !!currency))];
  return {
    leads: sum("leads"),
    spend: sum("spend"),
    impressions: sum("impressions"),
    clicks: sum("clicks"),
    days: eligible.length,
    currency: currencies.length === 1 ? currencies[0] : currencies.length > 1 ? "Mixed" : null,
  };
}

function formatDate(day: string) {
  const date = new Date(`${day}T00:00:00`);
  return Number.isNaN(date.getTime()) ? day : new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(date);
}

export function LeadBackfillDialog({ open, onOpenChange, brandId, accountId, onCommitted, onCloseAutoFocus, operation = "account" }: Props) {
  const identity = `${brandId}\u0000${accountId}\u0000${operation}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const onCommittedRef = useRef(onCommitted);
  onCommittedRef.current = onCommitted;
  const [context, setContext] = useState<BackfillContext | null>(null);
  const [job, setJob] = useState<LeadBackfillJob | null>(null);
  const currentJobRef = useRef<LeadBackfillJob | null>(null);
  currentJobRef.current = job;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pollError, setPollError] = useState("");
  const [working, setWorking] = useState(false);
  const [environmentConfirmation, setEnvironmentConfirmation] = useState("");
  const submitLock = useRef(false);
  const committedCountsRef = useRef<Record<string, number>>({});
  const eligibleDays = useMemo(() => job?.days.filter(day => day.status === "eligible") ?? [], [job]);
  const existingTotals = useMemo(() => job ? eligibleTotals(job, "existing") : null, [job]);
  const proposedTotals = useMemo(() => job ? eligibleTotals(job, "proposed") : null, [job]);
  const expired = !!job && Date.parse(job.expiresAt) <= Date.now();
  const active = !!job && ACTIVE.has(job.status);
  const detail = operation === "campaign_detail";
  const base = `/api/brands/${encodeURIComponent(brandId)}/ads-performance/${detail ? "lead-detail-recovery" : "lead-backfill"}`;

  const loadReviewState = useCallback(async () => {
    const requestIdentity = identity;
    if (!brandId || !accountId) {
      setError("Choose a connected ad account before opening lead backfill.");
      return;
    }
    setLoading(true);
    setError("");
    setPollError("");
    setContext(null);
    setJob(null);
    setEnvironmentConfirmation("");
    try {
      const query = `?accountId=${encodeURIComponent(accountId)}`;
      const [contextResult, jobResult] = await Promise.allSettled([
        requestJson<BackfillContext>(`${base}/context${query}`),
        requestJson<LeadBackfillJob | null>(`${base}/jobs${query}`),
      ]);
      if (identityRef.current !== requestIdentity) return;
      if (contextResult.status === "rejected") throw contextResult.reason;
      const nextContext = contextResult.value;
      if (nextContext.brandId !== brandId || nextContext.accountId !== accountId) throw new Error("The account context changed. Close this dialog and reopen it.");
      setContext(nextContext);
      // Keep verified prerequisites/readiness visible if job history fails,
      // but block preview until history is retried so active work isn't hidden.
      if (jobResult.status === "rejected") {
        setPollError(jobResult.reason instanceof Error ? jobResult.reason.message : "Job history could not be loaded.");
        return;
      }
      const latest = jobResult.value;
      if (latest && latest.brandId === brandId && latest.accountId === accountId) {
        setJob(latest);
        const committedCount = latest.days.filter(day => day.status === "committed").length;
        committedCountsRef.current[latest.id] = committedCount;
        if (committedCount > 0) void Promise.resolve(onCommittedRef.current());
      }
    } catch (cause) {
      if (identityRef.current === requestIdentity) setError(cause instanceof Error ? cause.message : "Could not load lead backfill context.");
    } finally {
      if (identityRef.current === requestIdentity) setLoading(false);
    }
  }, [accountId, base, brandId, identity]);

  useEffect(() => {
    if (open) void loadReviewState();
    else {
      setContext(null);
      setJob(null);
      setError("");
      setPollError("");
      setWorking(false);
      setEnvironmentConfirmation("");
      submitLock.current = false;
    }
  }, [open, identity, loadReviewState]);

  const fetchJob = useCallback(async (jobId: string) => {
    const requestIdentity = identity;
    const fresh = await requestJson<LeadBackfillJob>(`${base}/jobs/${encodeURIComponent(jobId)}`);
    if (identityRef.current !== requestIdentity || currentJobRef.current?.id !== jobId) return;
    if (fresh.brandId !== brandId || fresh.accountId !== accountId) {
      setJob(null);
      throw new Error("This job belongs to a different brand or account. It has been hidden.");
    }
    setJob(fresh);
    setPollError("");
    const committedCount = fresh.days.filter(day => day.status === "committed").length;
    const previousCount = committedCountsRef.current[fresh.id] ?? 0;
    committedCountsRef.current[fresh.id] = committedCount;
    if (committedCount > previousCount) {
      void Promise.resolve(onCommittedRef.current());
    }
    return fresh;
  }, [accountId, base, brandId, identity]);

  useEffect(() => {
    if (!open || !job || !ACTIVE.has(job.status)) return;
    let stopped = false;
    const timer = window.setInterval(async () => {
      if (stopped) return;
      try {
        await fetchJob(job.id);
      } catch (cause) {
        if (!stopped) setPollError(cause instanceof Error ? cause.message : "Progress could not be refreshed.");
      }
    }, 1800);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [fetchJob, job, open]);

  const createPreview = async () => {
    if (submitLock.current || working || !context) return;
    submitLock.current = true;
    setWorking(true);
    setError("");
    setPollError("");
    setJob(null);
    try {
      const created = await requestJson<LeadBackfillJob>(`${base}/preview`, {
        method: "POST",
        body: JSON.stringify({ accountId }),
      });
      if (identityRef.current !== identity) return;
      if (created.brandId !== brandId || created.accountId !== accountId) throw new Error("The preview returned for another account and has been hidden.");
      setJob(created);
      if (!ACTIVE.has(created.status)) await fetchJob(created.id);
    } catch (cause) {
      if (identityRef.current === identity) setError(cause instanceof Error ? cause.message : "Could not create a preview.");
    } finally {
      submitLock.current = false;
      setWorking(false);
    }
  };

  const confirmBackfill = async () => {
    if (submitLock.current || working || !job || job.brandId !== brandId || job.accountId !== accountId
      || !context || job.status !== "review" || expired || !job.approvalDigest || eligibleDays.length === 0) return;
    submitLock.current = true;
    setWorking(true);
    setError("");
    try {
      const updated = await requestJson<LeadBackfillJob>(`${base}/jobs/${encodeURIComponent(job.id)}/confirm`, {
        method: "POST",
        body: JSON.stringify({
          approvalDigest: job.approvalDigest,
          environmentConfirmation: environmentConfirmation.trim(),
          eligibleDates: eligibleDays.map(day => day.day),
        }),
      });
      if (identityRef.current !== identity) return;
      if (updated.brandId !== brandId || updated.accountId !== accountId) throw new Error("The confirmation returned for another account and has been hidden.");
      setJob(updated);
      const committedCount = updated.days.filter(day => day.status === "committed").length;
      const previousCount = committedCountsRef.current[updated.id] ?? 0;
      committedCountsRef.current[updated.id] = committedCount;
      if (committedCount > previousCount) {
        void Promise.resolve(onCommittedRef.current());
      }
    } catch (cause) {
      if (identityRef.current === identity) setError(cause instanceof Error ? cause.message : "Could not confirm this backfill.");
    } finally {
      submitLock.current = false;
      setWorking(false);
    }
  };

  const settingsMatch = !job || (job.definitionId === context?.definitionId && job.timezone === context?.timezone && job.environment === context?.environment);
  const displayContext = job ?? context;
  const canConfirm = job?.status === "review" && settingsMatch && !expired && !!job.approvalDigest
    && eligibleDays.length > 0 && environmentConfirmation.trim() === context?.environment;

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent onCloseAutoFocus={onCloseAutoFocus} className="min-w-0 max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-4xl gap-0 overflow-x-hidden overflow-y-auto p-0 sm:rounded-xl">
      <DialogHeader className="min-w-0 border-b border-slate-200 bg-slate-50/80 px-5 py-5 text-left sm:px-7">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-violet-700"><Database className="h-4 w-4"/> Lead data recovery</div>
        <DialogTitle className="text-2xl font-bold tracking-tight">{detail ? "Review campaign-detail lead recovery" : "Backfill verified lead events"}</DialogTitle>
        <DialogDescription className="min-w-0 max-w-2xl whitespace-normal break-words text-sm leading-relaxed">
          Review a fixed window of the last 60 completed account-local days. The report date selection does not change this range.
        </DialogDescription>
      </DialogHeader>
      <div className="min-w-0 space-y-5 px-5 py-5 sm:px-7 sm:py-6">
        {error && <Alert variant="destructive"><AlertCircle/><AlertTitle>Backfill could not continue</AlertTitle><AlertDescription className="flex flex-wrap items-center justify-between gap-2">{error}{!context && <Button size="sm" variant="outline" onClick={() => void loadReviewState()}>Retry loading context</Button>}</AlertDescription></Alert>}
        {loading && <div className="space-y-3" aria-label="Loading lead backfill context"><Skeleton className="h-16 w-full"/><Skeleton className="h-28 w-full"/></div>}
        {!loading && context && <>
          {detail && <><p className="text-sm">This separate operation fetches attributable campaign/ad facts for the saved event, including already-covered dates. It preserves account certificates and unrelated event totals. Provider omissions and unknown objectives stay unresolved; no account totals are redistributed.</p>
            <LeadDetailReadinessPanel base={base} accountId={accountId}/></>}
          <section className="grid min-w-0 gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2">
             <div className="min-w-0"><p className="text-xs font-medium uppercase tracking-wide text-slate-500">Brand / account</p><p className="mt-1 break-words font-semibold text-slate-900">{context.brandName} <span className="font-normal text-slate-400">/</span> {context.accountName}</p><p className="mt-1 break-all font-mono text-xs">{context.accountId}</p></div>
            <div className="min-w-0"><p className="text-xs font-medium uppercase tracking-wide text-slate-500">Event definition</p><p className="mt-1 break-words font-semibold text-slate-900">{displayContext!.eventName} <span title={displayContext!.definitionId} className="font-normal text-slate-500">· {displayContext!.definitionId.slice(0, 12)}</span></p></div>
            <div className="min-w-0"><p className="text-xs font-medium uppercase tracking-wide text-slate-500">Fixed account-local window</p><p className="mt-1 break-words font-mono text-sm font-semibold text-slate-900">{displayContext!.since} — {displayContext!.until}</p><p className="mt-1 break-words text-xs text-slate-500">{displayContext!.timezone}</p></div>
            <div><p className="text-xs font-medium uppercase tracking-wide text-slate-500">Target environment</p><p className="mt-1 inline-flex items-center gap-2 font-semibold capitalize text-slate-900"><span className={`h-2 w-2 rounded-full ${context.environment === "production" ? "bg-amber-500" : "bg-emerald-500"}`}/>{context.environment}</p></div>
          </section>
          {!job && <div className="flex flex-col items-start justify-between gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 sm:flex-row sm:items-center">
            <div><p className="font-semibold text-slate-900">No reviewable job</p><p className="mt-1 text-sm text-slate-600">Create a fresh preview to inspect eligible and blocked dates before writing anything.</p></div>
             {pollError && <p role="alert">{pollError} <button className="underline" onClick={() => void loadReviewState()}>Retry job history</button></p>}
             <Button onClick={() => void createPreview()} disabled={working || !!pollError}><RefreshCw className={`mr-2 h-4 w-4 ${working ? "animate-spin" : ""}`}/>{working ? "Creating preview…" : "Create preview"}</Button>
          </div>}
          {job && job.brandId === brandId && job.accountId === accountId && <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0"><p className="text-xs font-medium uppercase tracking-wide text-slate-500">Preview window</p><p className="mt-1 break-words font-mono text-sm font-semibold text-slate-900">{job.since} — {job.until} <span className="break-all font-sans font-normal text-slate-500">· {job.timezone}</span></p></div>
               <Badge variant={job.status === "completed" ? "default" : job.status === "partial" || job.status === "failed" ? "destructive" : "secondary"} className="gap-1.5 px-2.5 py-1"><span className={`h-1.5 w-1.5 rounded-full ${active ? "animate-pulse bg-violet-600" : "bg-current"}`}/>{detail ? statusCopy[job.status].replace(/Backfill/g, "Detail recovery") : statusCopy[job.status]}</Badge>
            </div>
            <p role="status" className="text-sm text-slate-600">{job.days.length} / 60 dates reviewed · {job.days.filter(day => day.status === "committed").length} committed · {eligibleDays.length} eligible · {job.days.filter(day => ["stale", "error"].includes(day.status)).length} need a new preview</p>
            {pollError && <Alert variant="destructive"><AlertCircle/><AlertTitle>Progress update failed</AlertTitle><AlertDescription className="flex flex-wrap items-center justify-between gap-2">{pollError}<Button size="sm" variant="outline" onClick={() => void fetchJob(job.id).catch(cause => setPollError(cause instanceof Error ? cause.message : "Retry failed."))}>Retry update</Button></AlertDescription></Alert>}
            {job.error && <Alert variant="destructive"><AlertCircle/><AlertTitle>Job reported an issue</AlertTitle><AlertDescription>{job.error}</AlertDescription></Alert>}
            {job.status === "interrupted" && <Alert><Clock3/><AlertTitle>Interrupted without losing review safety</AlertTitle><AlertDescription>This job did not finish. Start a fresh preview to re-check current source data and eligibility; no old approval will be reused.</AlertDescription></Alert>}
            {expired && job.status === "review" && <Alert variant="destructive"><Clock3/><AlertTitle>This preview has expired</AlertTitle><AlertDescription>Its approval can no longer be used. Create a fresh preview to review current data.</AlertDescription></Alert>}
            {!settingsMatch && <Alert variant="destructive"><AlertTitle>Settings changed since review</AlertTitle><AlertDescription>Create a new preview before confirming the current event or timezone.</AlertDescription></Alert>}
            <>
              <div className="min-w-0 overflow-hidden rounded-lg border border-slate-200">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <div><p className="font-semibold text-slate-900">Daily comparison</p><p className="text-xs text-slate-500">{job.days.length} dates · {eligibleDays.length} eligible · {job.days.filter(day => day.status === "blocked").length} blocked</p></div>
                  <span className="text-xs text-slate-500">Reviewed eligible-period proposal: {proposedTotals?.days ?? 0} days</span>
                </div>
                <div className="max-h-[360px] space-y-3 overflow-y-auto p-3 sm:hidden" data-testid="backfill-mobile-days">
                  {job.days.map(day => <article key={day.day} className="min-w-0 rounded-md border border-slate-200 p-3 text-xs">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2"><span className="font-mono">{day.day}</span><Badge variant="secondary">{day.status}</Badge></div>
                    <dl className="space-y-2 break-words"><div><dt className="font-semibold">Existing</dt><dd className="mt-1 text-slate-600">{valuesSummary(day.existing)}</dd></div><div><dt className="font-semibold">Proposed</dt><dd className="mt-1 text-slate-600">{valuesSummary(day.proposed)}</dd></div></dl>
                    {day.reason && <p className="mt-2 break-words text-slate-600">{day.reason}</p>}
                     {detail && <details className="mt-2"><summary>Campaign evidence: existing → proposed</summary><CampaignReadiness rows={day.readiness}/><hr className="my-2"/><CampaignReadiness rows={day.proposedReadiness}/></details>}
                  </article>)}
                </div>
                <div className="hidden min-w-0 max-h-[360px] overflow-auto sm:block">
                  <table className="w-full min-w-[760px] text-left text-xs">
                    <thead className="sticky top-0 z-10 bg-white text-[10px] uppercase tracking-wider text-slate-500 shadow-[0_1px_0_0_rgba(15,23,42,0.08)]"><tr><th className="px-4 py-2.5">Day</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Existing leads / spend / delivery</th><th className="px-4 py-2.5">Proposed leads / spend / delivery</th><th className="px-4 py-2.5">Outcome</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {job.days.map(day => <tr key={day.day} className="align-top">
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-slate-700">{formatDate(day.day)}</td>
                        <td className="px-4 py-3"><Badge variant={day.status === "eligible" || day.status === "committed" ? "secondary" : day.status === "blocked" ? "outline" : "destructive"}>{day.status}</Badge></td>
                        <td className="max-w-[230px] px-4 py-3 text-slate-600">{valuesSummary(day.existing)}</td>
                        <td className="max-w-[230px] px-4 py-3 text-slate-600">{valuesSummary(day.proposed)}</td>
                         <td className="max-w-[220px] px-4 py-3 text-slate-600">{day.reason || (day.status === "committed" ? "Committed" : day.status === "eligible" ? "Ready to include" : day.status === "stale" ? "Source changed since preview" : day.status === "error" ? "Could not process this day" : "Not eligible")}
                           {detail && <details className="mt-2"><summary>Campaign evidence: existing → proposed</summary><CampaignReadiness rows={day.readiness}/><hr className="my-2"/><CampaignReadiness rows={day.proposedReadiness}/></details>}
                         </td>
                      </tr>)}
                      {job.days.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">The preview has not returned any dates yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
                {existingTotals && proposedTotals && <div className="border-t border-slate-200 bg-slate-50/70 p-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Eligible period totals · {existingTotals.days} days</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {[
                      { label: "Existing", totals: existingTotals },
                      { label: "Proposed", totals: proposedTotals },
                    ].map(group => <div key={group.label}>
                      <p className="mb-1 text-xs font-semibold text-slate-700">{group.label}{group.totals.currency ? ` · ${group.totals.currency}` : ""}</p>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <p className="text-slate-500">Leads <span className="font-semibold text-slate-900">{group.totals.leads == null ? "Unknown" : count(group.totals.leads)}</span></p>
                        <p className="text-slate-500">Spend <span className="font-semibold text-slate-900">{group.totals.spend == null ? "Unknown" : money(group.totals.spend, group.totals.currency === "Mixed" ? null : group.totals.currency)}</span></p>
                        <p className="text-slate-500">Impressions <span className="font-semibold text-slate-900">{group.totals.impressions == null ? "Unknown" : count(group.totals.impressions)}</span></p>
                        <p className="text-slate-500">Clicks <span className="font-semibold text-slate-900">{group.totals.clicks == null ? "Unknown" : count(group.totals.clicks)}</span></p>
                      </div>
                    </div>)}
                  </div>
                </div>}
              </div>
            </>
            {(job.status === "review" || job.status === "interrupted" || job.status === "failed" || expired) && <section className="rounded-lg border border-violet-200 bg-violet-50/50 p-4">
              <div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-violet-700"/><div className="min-w-0 flex-1">
                <h3 className="font-semibold text-slate-900">{job.status === "review" && !expired ? "Confirm the reviewed eligible dates" : "Build a fresh preview"}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{job.status === "review" && !expired
                  ? `This will submit all ${eligibleDays.length} eligible dates exactly as listed above. Blocked dates are excluded.`
                  : "A fresh preview reads current source data and creates a new approval digest; prior approval is never carried forward."}</p>
                {job.status === "review" && !expired && <>
                  <label htmlFor="lead-backfill-environment" className="mt-4 block text-sm font-medium text-slate-800">Type <span className="font-mono">{context.environment}</span> to confirm the destination</label>
                  <Input id="lead-backfill-environment" autoComplete="off" value={environmentConfirmation} onChange={event => setEnvironmentConfirmation(event.target.value)} placeholder={`Enter ${context.environment}`} className="mt-1.5 max-w-sm bg-white"/>
                </>}
                <div className="mt-4 flex flex-wrap gap-2">
                  {job.status === "review" && !expired && <Button onClick={() => void confirmBackfill()} disabled={!canConfirm || working || active}><Check className="mr-2 h-4 w-4"/>{working ? "Confirming…" : `Confirm ${eligibleDays.length} eligible days`}</Button>}
                  <Button variant="outline" onClick={() => void createPreview()} disabled={working || active}><RefreshCw className={`mr-2 h-4 w-4 ${working ? "animate-spin" : ""}`}/>{working ? "Creating preview…" : "Create a fresh preview"}</Button>
                </div>
              </div></div>
            </section>}
            {(job.status === "completed" || job.status === "partial") && <Alert><Check/><AlertTitle>{job.status === "completed" ? "Eligible lead dates are committed" : "Some eligible dates were committed"}</AlertTitle><AlertDescription>Performance Brief and settings were refreshed. Blocked, stale, or failed dates remain visible above and are not silently retried.</AlertDescription></Alert>}
            {(job.status === "completed" || job.status === "partial") && <Button variant="outline" onClick={() => void createPreview()} disabled={working || active}>Create a fresh preview</Button>}
            {active && <div className="flex items-center gap-2 rounded-md bg-violet-50 px-3 py-2 text-sm text-violet-800"><RefreshCw className="h-4 w-4 animate-spin"/>Progress refreshes automatically. You can safely close this dialog and return to the latest job later.</div>}
          </>}
        </>}
        {!loading && !context && !error && <Skeleton className="h-24 w-full"/>}
      </div>
      <div className="flex justify-end border-t border-slate-200 bg-slate-50/70 px-5 py-3 sm:px-7"><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></div>
    </DialogContent>
  </Dialog>;
}

export default LeadBackfillDialog;